import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { API_URL } from '../config/api';
import { 
  PiggyBank, Plus, TrendingUp, TrendingDown, Trash2, 
  FileText, Lightbulb, Edit3, Sparkles, Calendar, X, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import CreatePortefeuilleWizard from './CreatePortefeuilleWizard';
import EditPortefeuilleDialog from './EditPortefeuilleDialog';
import DepotPortefeuilleDialog from './DepotPortefeuilleDialog';
import DepensePortefeuilleDialog from './DepensePortefeuilleDialog';
import DeletePortefeuilleDialog from './DeletePortefeuilleDialog';
import DetailsPortefeuilleDialog from './DetailsPortefeuilleDialog';
import useTips from '../hooks/useTips';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// Coach financial tip component - Premium & Dismissable
const CoachTip = ({ onDismiss }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="premium-card relative mb-5"
  >
    {/* Close button */}
    {onDismiss && (
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-muted transition-colors z-10"
        aria-label="Masquer cette astuce"
      >
        <X className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
      </button>
    )}
    
    <div className="flex items-start gap-3 pr-8">
      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
        <Lightbulb className="h-5 w-5 text-primary" strokeWidth={1.5} />
      </div>
      <div className="space-y-2 flex-1">
        <h3 className="text-sm font-semibold text-foreground tracking-tight">
          À quoi sert un portefeuille ?
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Un portefeuille te permet de <strong className="text-foreground">prévoir une somme d'argent</strong> pour une dépense précise 
          afin d'éviter les imprévus.
        </p>
        <div className="bg-muted/30 rounded-lg p-2.5 space-y-1.5 text-xs text-foreground/70">
          <p>
            <span className="mr-1.5">🍽️</span>
            <strong className="text-primary">Exemple :</strong> 8€ par jour pour les repas du midi = 160€ par mois
          </p>
          <p>
            <span className="mr-1.5">🎁</span>
            <strong className="text-accent">Autre idée :</strong> 20€ par semaine pour un cadeau = 80€ en 1 mois
          </p>
        </div>
      </div>
    </div>
  </motion.div>
);

// Portefeuille Card Component with modern design
const PortefeuilleCard = ({ portefeuille, onDeposit, onSpend, onEdit, onDelete, onDetails }) => {
  const formatCurrency = (amount) => {
    // Protection contre NaN : utiliser 0 si la valeur est undefined, null ou NaN
    const safeAmount = (amount === undefined || amount === null || isNaN(amount)) ? 0 : amount;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(safeAmount);
  };

  const getFrequencyLabel = (frequence) => {
    switch (frequence) {
      case 'hebdomadaire': return 'chaque semaine';
      case 'mensuel': return 'chaque mois';
      case 'tous les 15 jours': return 'tous les 15 jours';
      default: return frequence;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className="bg-white border-2 border-teal-200 hover:border-teal-400 rounded-2xl overflow-hidden shadow-lg shadow-teal-500/10 hover:shadow-teal-500/20 transition-all"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <PiggyBank className="h-4 w-4 text-white" />
            </div>
            <h3 className="font-bold text-white truncate">{portefeuille.nom}</h3>
          </div>
          <button 
            onClick={() => onEdit(portefeuille)}
            className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
          >
            <Edit3 className="h-4 w-4 text-white/80" />
          </button>
        </div>
        <div className="mt-1">
          {portefeuille.type === 'recurrente' ? (
            <span className="text-xs text-white/80 flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatCurrency(portefeuille.montant_recurrent)} {getFrequencyLabel(portefeuille.frequence)}
            </span>
          ) : (
            <span className="text-xs text-white/80">Ponctuelle</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Balance */}
        <div className="text-center py-4 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl mb-4">
          <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            {formatCurrency(portefeuille.solde)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Solde actuel</div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDeposit(portefeuille)}
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-10"
            >
              <TrendingUp className="h-4 w-4 mr-1" />
              Dépôt
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSpend(portefeuille)}
              className="text-amber-600 border-amber-200 hover:bg-amber-50 h-10"
            >
              <TrendingDown className="h-4 w-4 mr-1" />
              Dépenser
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(portefeuille)}
              className="text-rose-600 border-rose-200 hover:bg-rose-50 h-10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDetails(portefeuille)}
            className="w-full text-gray-600 border-gray-200 hover:bg-gray-50 h-10"
          >
            <FileText className="h-4 w-4 mr-2" />
            Voir les détails
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Empty State Component
const EmptyState = ({ onCreateClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gradient-to-br from-teal-50 via-cyan-50 to-amber-50 border-2 border-dashed border-teal-300 rounded-2xl p-8"
  >
    <div className="text-center space-y-4">
      <motion.div 
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', delay: 0.2 }}
        className="bg-gradient-to-br from-teal-500 to-cyan-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-xl shadow-teal-500/30"
      >
        <PiggyBank className="h-10 w-10 text-white" />
      </motion.div>
      <div>
        <h3 className="text-xl font-bold text-teal-800">Prêt à anticiper ? 🚀</h3>
        <p className="text-sm text-gray-600 mt-2 max-w-sm mx-auto">
          Crée ta première portefeuille pour commencer à mettre de l'argent de côté. 
          C'est le premier pas vers un budget maîtrisé !
        </p>
      </div>
      <Button
        onClick={onCreateClick}
        className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 shadow-lg shadow-teal-500/30 h-12 px-6"
        data-testid="create-first-portefeuille-btn"
      >
        <Plus className="h-5 w-5 mr-2" />
        Créer ma première portefeuille
      </Button>
    </div>
  </motion.div>
);

const Portefeuilles = ({ calendar, currentBalance, onDataChanged }) => {
  const [portefeuilles, setPortefeuilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [selectedPortefeuille, setSelectedPortefeuille] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  
  // Tips system
  const { tips, dismissTip, loading: tipsLoading } = useTips('portefeuille');
  const showIntroTip = tips.some(t => t.id === 'portefeuille_intro');

  useEffect(() => {
    if (calendar) {
      loadPortefeuilles();
    }
  }, [calendar]);

  const loadPortefeuilles = async () => {
    try {
      const response = await axios.get(
        `${API}/portefeuilles/${calendar.id}`,
        { withCredentials: true }
      );
      setPortefeuilles(response.data);
    } catch (error) {
      console.error('Error loading portefeuilles:', error);
      toast.error('Erreur lors du chargement des portefeuilles');
    } finally {
      setLoading(false);
    }
  };

  const handleDismissTip = async () => {
    const success = await dismissTip('portefeuille_intro', 'portefeuille');
    if (success) {
      toast.success('Astuce masquée - retrouve-la dans l\'onglet Astuces');
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateWizard(false);
    // Immediately reload portefeuilles for instant feedback
    loadPortefeuilles();
    // Trigger calendar refresh for immediate sync
    if (onDataChanged) onDataChanged();
  };

  const handleActionSuccess = async () => {
    // Close dialog first for immediate visual feedback
    setDialogType(null);
    setSelectedPortefeuille(null);
    // Then reload data
    await loadPortefeuilles();
    // Trigger calendar refresh for immediate sync
    if (onDataChanged) onDataChanged();
  };

  const openDialog = (type, portefeuille) => {
    setSelectedPortefeuille(portefeuille);
    setDialogType(type);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <motion.div 
          className="flex flex-col items-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div 
            className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <p className="text-muted-foreground text-sm">Chargement...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Coach Tip - Dismissable */}
      <AnimatePresence>
        {showIntroTip && (
          <CoachTip onDismiss={handleDismissTip} />
        )}
      </AnimatePresence>

      {/* Header - Premium */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h2 className="text-lg font-semibold text-foreground tracking-tight flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" strokeWidth={1.5} />
            Mes Portefeuilles
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {portefeuilles.length} portefeuille{portefeuilles.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateWizard(true)}
          className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 w-full sm:w-auto h-12 sm:h-11 shadow-lg shadow-teal-500/30 touch-manipulation"
          data-testid="create-portefeuille-btn"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle portefeuille
        </Button>
      </motion.div>

      {/* Portefeuilles Grid */}
      {portefeuilles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {portefeuilles.map((portefeuille, index) => (
              <motion.div
                key={portefeuille.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <PortefeuilleCard
                  portefeuille={portefeuille}
                  onDeposit={(c) => openDialog('depot', c)}
                  onSpend={(c) => openDialog('depense', c)}
                  onEdit={(c) => openDialog('edit', c)}
                  onDelete={(c) => openDialog('delete', c)}
                  onDetails={(c) => openDialog('details', c)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <EmptyState onCreateClick={() => setShowCreateWizard(true)} />
      )}

      {/* Create Wizard */}
      <CreatePortefeuilleWizard
        open={showCreateWizard}
        onClose={() => setShowCreateWizard(false)}
        calendarId={calendar?.id}
        currentBalance={currentBalance}
        onSuccess={handleCreateSuccess}
      />

      {/* Edit Dialog */}
      {selectedPortefeuille && dialogType === 'edit' && (
        <EditPortefeuilleDialog
          open={true}
          onClose={() => { setDialogType(null); setSelectedPortefeuille(null); }}
          portefeuille={selectedPortefeuille}
          currentBalance={currentBalance}
          onSuccess={handleActionSuccess}
        />
      )}

      {/* Deposit Dialog */}
      {selectedPortefeuille && dialogType === 'depot' && (
        <DepotPortefeuilleDialog
          open={true}
          onClose={() => { setDialogType(null); setSelectedPortefeuille(null); }}
          portefeuille={selectedPortefeuille}
          onSuccess={handleActionSuccess}
        />
      )}

      {/* Spend Dialog */}
      {selectedPortefeuille && dialogType === 'depense' && (
        <DepensePortefeuilleDialog
          open={true}
          onClose={() => { setDialogType(null); setSelectedPortefeuille(null); }}
          portefeuille={selectedPortefeuille}
          onSuccess={handleActionSuccess}
        />
      )}

      {/* Delete Dialog */}
      {selectedPortefeuille && dialogType === 'delete' && (
        <DeletePortefeuilleDialog
          open={true}
          onClose={() => { setDialogType(null); setSelectedPortefeuille(null); }}
          portefeuille={selectedPortefeuille}
          onSuccess={handleActionSuccess}
        />
      )}

      {/* Details Dialog */}
      {selectedPortefeuille && dialogType === 'details' && (
        <DetailsPortefeuilleDialog
          open={true}
          onClose={() => { setDialogType(null); setSelectedPortefeuille(null); }}
          portefeuille={selectedPortefeuille}
        />
      )}
    </div>
  );
};

export default Portefeuilles;
